/**
 * @fileoverview Embedded building-owned blocks (`systems`, `intel`,
 *               `riskProfile`, `riskAssessment`) for the complex-inspections
 *               inspectable-entity contract.
 * @module import-core/contracts/complex-inspections/embeddedBuildingSchemas
 *
 * Responsibilities:
 * - Validate the polymorphic `inspectableEntities.systems[]` block: every
 *   building system (alarm, extinguishing, backup power, ...) with its
 *   events, tests, history, child items, and attachment references.
 * - Validate the `intel`, `riskProfile`, and `riskAssessment` blocks that sit
 *   beside `systems` on the same entity record.
 *
 * Dependencies:
 * - `zod` v3 only. These schemas mirror the canonical embedded contract types
 *   in `@firesquirrel/sync-config` (`embeddedBuildingSystem.ts`); they do not
 *   import them, so this contract source stays free of extra dependencies
 *   (mobile and web packages alias this folder's source).
 *
 * Architecture:
 * - Consumed by `entitySchemas.ts`, which adds the four blocks to the
 *   client-writable projection and (by extension) the complete persisted
 *   document schema. Hosts inherit them; none redefines them.
 * - Every embedded schema is `.passthrough()` deliberately: unknown legacy
 *   extension fields, unresolved lookup codes, and false/zero/null
 *   distinctions must survive a round-trip (G-016).
 *
 * Notes:
 * - Do NOT add defaults to embedded fields: a default rewrites data the client
 *   did not send.
 * - Moved here verbatim from the tenant-api host schema so the embedded blocks
 *   have exactly one definition.
 */

import { z } from 'zod';
import { isoDateLikeSchema } from './sharedSchemas';

const embeddedLookupReferenceSchema = z
  .object({
    lookupId: z.string().optional(),
    lookupType: z.string().optional(),
    code: z.string().optional(),
    name: z.string().optional(),
    rawCode: z.string().optional(),
  })
  .passthrough();

const embeddedSourceProvenanceSchema = z
  .object({
    system: z.string().optional(),
    table: z.string().optional(),
    sourceId: z.union([z.string(), z.number()]).optional(),
    legacyDb: z.string().optional(),
    db: z.string().optional(),
    deptId: z.number().optional(),
    runId: z.string().optional(),
    migratedAt: isoDateLikeSchema.optional(),
    importedAt: isoDateLikeSchema.optional(),
  })
  .passthrough();

const embeddedAuditFields = {
  createdAt: isoDateLikeSchema.optional(),
  createdBy: z.string().optional(),
  updatedAt: isoDateLikeSchema.optional(),
  updatedBy: z.string().optional(),
  version: z.number().optional(),
};

const embeddedTombstoneFields = {
  _deleted: z.boolean().optional(),
  deletedAt: isoDateLikeSchema.optional(),
  deletedBy: z.string().optional(),
};

const embeddedAttachmentRefSchemaObject = z
  .object({
    ...embeddedAuditFields,
    ...embeddedTombstoneFields,
    _id: z.string().optional(),
    attachmentId: z.string().optional(),
    parentType: z.string().optional(),
    parentId: z.string().optional(),
    parentPath: z.string().optional(),
    kind: z.string().optional(),
    fileName: z.string().optional(),
    blobName: z.string().optional(),
    blobUrl: z.string().optional(),
    mimeType: z.string().optional(),
    contentType: z.string().optional(),
    fileSize: z.number().optional(),
    sizeBytes: z.number().optional(),
    sha256: z.string().optional(),
    status: z.string().optional(),
    tags: z.array(z.string()).optional(),
    uploadedAt: isoDateLikeSchema.optional(),
    uploadedBy: z.string().optional(),
    caption: z.string().optional(),
    description: z.string().optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
    legacySource: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedSystemEventSchema = z
  .object({
    ...embeddedAuditFields,
    ...embeddedTombstoneFields,
    eventId: z.string().optional(),
    eventType: embeddedLookupReferenceSchema.optional(),
    occurredAt: isoDateLikeSchema.optional(),
    receivedAt: isoDateLikeSchema.optional(),
    contractor: embeddedLookupReferenceSchema.optional(),
    description: z.string().optional(),
    expiresAt: isoDateLikeSchema.optional(),
    passed: z.boolean().nullable().optional(),
    notes: z.string().optional(),
    attachments: z.array(embeddedAttachmentRefSchemaObject).optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedSystemTestSchema = z
  .object({
    ...embeddedAuditFields,
    ...embeddedTombstoneFields,
    testId: z.string().optional(),
    testType: z.string().optional(),
    agency: z.string().optional(),
    inspector: z.string().optional(),
    testedAt: isoDateLikeSchema.optional(),
    inspectedAt: isoDateLikeSchema.optional(),
    expiresAt: isoDateLikeSchema.optional(),
    score: z.union([z.number(), z.string()]).nullable().optional(),
    passed: z.boolean().nullable().optional(),
    notes: z.string().optional(),
    attachments: z.array(embeddedAttachmentRefSchemaObject).optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedSystemChildItemSchema = z
  .object({
    ...embeddedAuditFields,
    ...embeddedTombstoneFields,
    childId: z.string().optional(),
    kind: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    spec: z.record(z.unknown()).optional(),
    attachments: z.array(embeddedAttachmentRefSchemaObject).optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedSystemLinkSchema = z
  .object({
    systemId: z.string().optional(),
    targetId: z.string().optional(),
    relationship: z.string().optional(),
    name: z.string().optional(),
  })
  .passthrough();

const embeddedSystemChildItemsSchema = z
  .object({
    chemicals: z.array(embeddedSystemChildItemSchema).optional(),
    tanks: z.array(embeddedSystemChildItemSchema).optional(),
    keyBoxes: z.array(embeddedSystemChildItemSchema).optional(),
    panels: z.array(embeddedSystemChildItemSchema).optional(),
    detectors: z.array(embeddedSystemChildItemSchema).optional(),
    equipment: z.array(embeddedSystemChildItemSchema).optional(),
    alarms: z.array(embeddedSystemChildItemSchema).optional(),
    pumps: z.array(embeddedSystemChildItemSchema).optional(),
    backupPower: z.array(embeddedSystemLinkSchema).optional(),
    permits: z.array(embeddedSystemLinkSchema).optional(),
  })
  .passthrough();

const embeddedBuildingSystemSchemaObject = z
  .object({
    ...embeddedAuditFields,
    ...embeddedTombstoneFields,
    systemId: z.string().optional(),
    category: z.string().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    notes: z.string().optional(),
    status: embeddedLookupReferenceSchema.optional(),
    spec: z.record(z.unknown()).optional(),
    childItems: embeddedSystemChildItemsSchema.optional(),
    events: z.array(embeddedSystemEventSchema).optional(),
    tests: z.array(embeddedSystemTestSchema).optional(),
    history: z.array(embeddedSystemTestSchema).optional(),
    attachments: z.array(embeddedAttachmentRefSchemaObject).optional(),
    sourceTable: z.string().optional(),
    sourceId: z.union([z.string(), z.number()]).optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
    legacySource: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedRiskFactorSchema = z
  .object({
    factorId: z.string().optional(),
    label: z.string().optional(),
    points: z.number().nullable().optional(),
    selection: embeddedLookupReferenceSchema.optional(),
    evidenceSystemIds: z.array(z.string()).optional(),
    notes: z.string().optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedRiskAssessmentSchemaObject = z
  .object({
    ...embeddedAuditFields,
    factors: z.array(embeddedRiskFactorSchema).optional(),
    policyVersion: z.string().optional(),
    total: z.number().nullable().optional(),
    rank: z.string().optional(),
    color: z.string().optional(),
    manual: z.boolean().optional(),
    complete: z.boolean().optional(),
    reviewedAt: isoDateLikeSchema.optional(),
    reviewedBy: z.string().optional(),
    assessedAt: isoDateLikeSchema.optional(),
    assessedBy: z.string().optional(),
    notes: z.string().optional(),
    provenance: embeddedSourceProvenanceSchema.optional(),
  })
  .passthrough();

const embeddedRiskProfileSchemaObject = z
  .object({
    score: z.number().optional(),
    isManual: z.boolean().optional(),
    isComplete: z.boolean().optional(),
    assessedAt: isoDateLikeSchema.optional(),
    notes: z.string().optional(),
    rank: z.string().optional(),
    color: z.string().optional(),
  })
  .passthrough();

const intelSnapshotSchemaObject = z
  .object({
    intelId: z.string().optional(),
    category: z.union([z.string(), z.object({ name: z.string().optional() }).passthrough()]).optional(),
    content: z.string().optional(),
    // Imported rows persist this as a BSON Date; accept both Date and ISO string.
    createdAt: isoDateLikeSchema.optional(),
    attachments: z.array(embeddedAttachmentRefSchemaObject).optional(),
  })
  .passthrough();

const embeddedPermitSnapshotSchemaObject = z
  .object({
    permitId: z.string().optional(),
    permitNumber: z.string().optional(),
    permitType: embeddedLookupReferenceSchema.optional(),
    status: embeddedLookupReferenceSchema.optional(),
    issuedAt: isoDateLikeSchema.optional(),
    effectiveAt: isoDateLikeSchema.optional(),
    expiresAt: isoDateLikeSchema.optional(),
    manualExpire: z.boolean().optional(),
    manualFee: z.boolean().optional(),
    sqFt: z.number().optional(),
    occupancyLoad: z.number().optional(),
    cerisId: z.string().optional(),
    financials: z.record(z.unknown()).optional(),
    notes: z.string().optional(),
    legacySource: embeddedSourceProvenanceSchema.optional(),
    attachments: z.array(embeddedAttachmentRefSchemaObject).optional(),
  })
  .passthrough();

const embeddedTankSnapshotSchemaObject = z
  .object({
    tankId: z.string().optional(),
    tankNumber: z.string().optional(),
    description: z.string().optional(),
    registration: z.string().optional(),
    contents: z.string().optional(),
    capacity: z.union([z.number(), z.string()]).optional(),
    unit: z.string().optional(),
    placement: z.string().optional(),
    construction: z.string().optional(),
    approval: z.string().optional(),
    position: z.string().optional(),
    location: z.string().optional(),
    usage: z.string().optional(),
    leakDetection: z.string().optional(),
    leakDetectionMonitored: z.boolean().optional(),
    baseConstruction: z.string().optional(),
    pipesAbove: z.boolean().optional(),
    pipesBelow: z.boolean().optional(),
    pipeConstruction: z.string().optional(),
    primaryContainment: z.string().optional(),
    secondaryContainment: z.string().optional(),
    dateBuilt: isoDateLikeSchema.optional(),
    dateInstalled: isoDateLikeSchema.optional(),
    dateRemoved: isoDateLikeSchema.optional(),
    manufacturer: z.string().optional(),
    status: embeddedLookupReferenceSchema.optional(),
    notes: z.string().optional(),
    attachments: z.array(embeddedAttachmentRefSchemaObject).optional(),
  })
  .passthrough();

const embeddedChemicalSnapshotSchemaObject = z
  .object({
    chemicalId: z.string().optional(),
    chemicalName: z.string().optional(),
    casNumber: z.string().optional(),
    hazardClass: z.string().optional(),
    quantity: z.union([z.number(), z.string()]).optional(),
    unit: z.string().optional(),
    locationOnSite: z.string().optional(),
    unNumber: z.string().optional(),
    noaaNumber: z.string().optional(),
    nfpa: z.record(z.unknown()).optional(),
    flashPointValue: z.union([z.number(), z.string()]).optional(),
    flashPointRange: z.string().optional(),
    description: z.string().optional(),
    physicalState: z.record(z.unknown()).optional(),
    hazardFlags: z.record(z.unknown()).optional(),
    maxAmount: z.string().optional(),
    aveAmount: z.string().optional(),
    reportedQuantity: z.union([z.number(), z.string()]).optional(),
    reportedUnit: z.string().optional(),
    exempt: z.boolean().optional(),
    daysOnHand: z.number().optional(),
    storageLocation: z.string().optional(),
    storageType: z.string().optional(),
    storagePressure: z.string().optional(),
    storageTemperature: z.string().optional(),
    notes: z.string().optional(),
    attachments: z.array(embeddedAttachmentRefSchemaObject).optional(),
  })
  .passthrough();

const embeddedLegacyHistorySnapshotSchemaObject = z
  .object({
    historyId: z.string().optional(),
    date: isoDateLikeSchema.optional(),
    type: z.string().optional(),
    inspector: z.string().optional(),
    result: z.string().optional(),
    violationsFound: z.number().optional(),
    notes: z.string().optional(),
  })
  .passthrough();

const embeddedCommunicationLogSnapshotSchemaObject = z
  .object({
    logId: z.string().optional(),
    date: isoDateLikeSchema.optional(),
    occurredAt: isoDateLikeSchema.optional(),
    type: z.string().optional(),
    direction: z.string().optional(),
    summary: z.string().optional(),
    notes: z.string().optional(),
  })
  .passthrough();

const embeddedTceSnapshotSchemaObject = z
  .object({
    premiseId: z.string().optional(),
    referenceNumber: z.string().optional(),
    status: z.string().optional(),
    name: z.string().optional(),
    address: z.string().optional(),
    address2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
    url: z.string().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    profiles: z.array(z.record(z.unknown())).optional(),
    inventory: z.array(z.record(z.unknown())).optional(),
  })
  .passthrough();

const embeddedExternalLinksSchemaObject = z
  .object({
    tce: embeddedTceSnapshotSchemaObject.optional(),
  })
  .passthrough();


// ---------------------------------------------------------------------------
// Public exports
//
// The object schemas above carry deeply nested inferred types. Exported as-is
// they push the complete entity document schema past the TypeScript
// declaration-emit limit (TS7056). The runtime schemas are unchanged; only the
// public static type is simplified to the identifying keys plus an open index,
// which matches the passthrough behavior (unknown fields are preserved).
// ---------------------------------------------------------------------------

/** Open record: any key a passthrough embedded block may carry. */
type OpenEmbeddedBlock = { [key: string]: unknown };

/** One embedded building system (alarm, extinguishing, backup power, ...). */
export interface EmbeddedBuildingSystemBlock extends OpenEmbeddedBlock {
  systemId?: string;
  category?: string;
  name?: string;
  description?: string;
  notes?: string;
  spec?: Record<string, unknown>;
  childItems?: Record<string, unknown>;
  events?: Record<string, unknown>[];
  tests?: Record<string, unknown>[];
  history?: Record<string, unknown>[];
  attachments?: Record<string, unknown>[];
}

/** Building intel snapshot entry. */
export interface IntelSnapshotBlock extends OpenEmbeddedBlock {
  intelId?: string;
  content?: string;
  createdAt?: Date | string;
}

/** Risk display summary. */
export interface EmbeddedRiskProfileBlock extends OpenEmbeddedBlock {
  score?: number;
  rank?: string;
  color?: string;
}

/** Ten-factor risk assessment. */
export interface EmbeddedRiskAssessmentBlock extends OpenEmbeddedBlock {
  factors?: Record<string, unknown>[];
  total?: number | null;
  rank?: string;
  color?: string;
}

/** Embedded permit snapshot on inspectableEntities.permits[]. */
export interface EmbeddedPermitSnapshotBlock extends OpenEmbeddedBlock {
  permitId?: string;
  permitNumber?: string;
}

/** Embedded tank snapshot on inspectableEntities.tanks[]. */
export interface EmbeddedTankSnapshotBlock extends OpenEmbeddedBlock {
  tankId?: string;
  tankNumber?: string;
}

/** Embedded chemical snapshot on inspectableEntities.chemicals[]. */
export interface EmbeddedChemicalSnapshotBlock extends OpenEmbeddedBlock {
  chemicalId?: string;
  chemicalName?: string;
}

/** Embedded legacy history snapshot on inspectableEntities.legacyHistory[]. */
export interface EmbeddedLegacyHistorySnapshotBlock extends OpenEmbeddedBlock {
  historyId?: string;
}

/** Embedded communication log snapshot on inspectableEntities.communicationLogs[]. */
export interface EmbeddedCommunicationLogSnapshotBlock extends OpenEmbeddedBlock {
  logId?: string;
}

/** Embedded TCE compliance snapshot on inspectableEntities.tce / externalLinks.tce. */
export interface EmbeddedTceSnapshotBlock extends OpenEmbeddedBlock {
  premiseId?: string;
}

/** Embedded external links block on inspectableEntities.externalLinks. */
export interface EmbeddedExternalLinksBlock extends OpenEmbeddedBlock {
  tce?: EmbeddedTceSnapshotBlock;
}

/** Embedded attachment reference on inspectableEntities.attachments[]. */
export interface EmbeddedAttachmentRefBlock extends OpenEmbeddedBlock {
  _id?: string;
  attachmentId?: string;
  fileName?: string;
}

export const embeddedBuildingSystemSchema =
  embeddedBuildingSystemSchemaObject as unknown as z.ZodType<EmbeddedBuildingSystemBlock>;
export const intelSnapshotSchema =
  intelSnapshotSchemaObject as unknown as z.ZodType<IntelSnapshotBlock>;
export const embeddedRiskProfileSchema =
  embeddedRiskProfileSchemaObject as unknown as z.ZodType<EmbeddedRiskProfileBlock>;
export const embeddedRiskAssessmentSchema =
  embeddedRiskAssessmentSchemaObject as unknown as z.ZodType<EmbeddedRiskAssessmentBlock>;
export const embeddedPermitSnapshotSchema =
  embeddedPermitSnapshotSchemaObject as unknown as z.ZodType<EmbeddedPermitSnapshotBlock>;
export const embeddedTankSnapshotSchema =
  embeddedTankSnapshotSchemaObject as unknown as z.ZodType<EmbeddedTankSnapshotBlock>;
export const embeddedChemicalSnapshotSchema =
  embeddedChemicalSnapshotSchemaObject as unknown as z.ZodType<EmbeddedChemicalSnapshotBlock>;
export const embeddedLegacyHistorySnapshotSchema =
  embeddedLegacyHistorySnapshotSchemaObject as unknown as z.ZodType<EmbeddedLegacyHistorySnapshotBlock>;
export const embeddedCommunicationLogSnapshotSchema =
  embeddedCommunicationLogSnapshotSchemaObject as unknown as z.ZodType<EmbeddedCommunicationLogSnapshotBlock>;
export const embeddedTceSnapshotSchema =
  embeddedTceSnapshotSchemaObject as unknown as z.ZodType<EmbeddedTceSnapshotBlock>;
export const embeddedExternalLinksSchema =
  embeddedExternalLinksSchemaObject as unknown as z.ZodType<EmbeddedExternalLinksBlock>;
export const embeddedAttachmentRefSchema =
  embeddedAttachmentRefSchemaObject as unknown as z.ZodType<EmbeddedAttachmentRefBlock>;
